'use client';

import { Modal, Switch } from '@mantine/core';
import { IconCookie } from '@tabler/icons-react';
import { useCallback, useEffect, useState } from 'react';
import {
  CookiePreferences,
  getCookiePreferences,
  getDefaultPreferences,
  hasConsented,
  setCookiePreferences,
} from './utils';
import { useCookieSettingsListener } from './useCookieSettings';
import { Button } from '@scaleup/frontend/ui';
import { DateTime } from 'luxon';

export const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>(getDefaultPreferences());

  useEffect(() => {
    // Check if user has already consented
    const hasUserConsented = hasConsented();
    if (!hasUserConsented) {
      // Show banner after a short delay for better UX
      setTimeout(() => setShowBanner(true), 1000);
    } else {
      // Load existing preferences
      const existing = getCookiePreferences();
      if (existing) {
        setPreferences(existing);
      }
    }
  }, []);

  // Listen for external requests to open cookie settings
  const handleOpenSettings = useCallback(() => {
    const existing = getCookiePreferences();
    if (existing) {
      setPreferences(existing);
    }
    setShowSettings(true);
  }, []);

  useCookieSettingsListener(handleOpenSettings);

  const handleAcceptAll = () => {
    const allAccepted: CookiePreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true,
      timestamp: DateTime.now().toUnixInteger(),
    };
    setCookiePreferences(allAccepted);
    setPreferences(allAccepted);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleAcceptNecessary = () => {
    const necessaryOnly = getDefaultPreferences();
    setCookiePreferences(necessaryOnly);
    setPreferences(necessaryOnly);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleSavePreferences = () => {
    setCookiePreferences(preferences);
    setShowBanner(false);
    setShowSettings(false);
  };

  // Removed duplicate handleOpenSettings declaration to fix redeclaration error

  const togglePreference = (category: keyof CookiePreferences) => {
    if (category === 'necessary' || category === 'timestamp') return; // Can't disable necessary cookies
    setPreferences((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  if (!showBanner && !showSettings) return null;

  return (
    <>
      {/* Cookie Banner */}
      {showBanner && !showSettings && (
        <div className="fixed bottom-0 left-0 right-0 z-[1100] bg-white border-t-2 border-gray-200 shadow-lg">
          <div className="max-w-7xl mx-auto p-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-lg bg-primary-100 flex items-center justify-center">
                  <IconCookie size={24} className="text-primary-600" />
                </div>
              </div>

              <div className="flex-1">
                <h3 className="text-lg font-bold text-text-primary mb-2">
                  We Value Your Privacy
                </h3>
                <p className="text-text-secondary text-sm mb-4">
                  We use cookies to enhance your browsing experience, serve personalized content,
                  and analyze our traffic. By clicking "Accept All", you consent to our use of cookies.
                  You can manage your preferences or learn more in our{' '}
                  <a href="/privacy-policy" className="text-brand-primary hover:underline">
                    Privacy Policy
                  </a>
                  .
                </p>

                <div className="flex flex-wrap gap-3">
                  <Button
                    variant="primary"
                    text="Accept All"
                    onClick={handleAcceptAll}
                  />
                  <Button
                    variant="secondary"
                    text="Necessary Only"
                    onClick={handleAcceptNecessary}
                  />
                  <button
                    onClick={handleOpenSettings}
                    className="px-4 py-2 text-text-primary hover:text-brand-primary font-semibold"
                  >
                    Cookie Settings
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cookie Settings Modal */}
      <Modal
        opened={showSettings}
        onClose={() => setShowSettings(false)}
        title="Cookie Settings"
        size="lg"
        centered
        zIndex={1100}
      >
        <div className="flex flex-col gap-6">
          <p className="text-text-secondary">
            Manage your cookie preferences. Necessary cookies are required for the website to function
            and cannot be disabled.
          </p>

          {/* Necessary Cookies */}
          <div className="border-b border-gray-200 pb-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h4 className="font-bold text-text-primary">Necessary Cookies</h4>
                <p className="text-sm text-text-secondary mt-1">
                  Required for the website to function properly. These cookies ensure basic functionalities
                  and security features.
                </p>
              </div>
              <Switch
                checked={true}
                disabled
                classNames={{ track: 'cursor-not-allowed' }}
              />
            </div>
          </div>

          {/* Analytics Cookies */}
          <div className="border-b border-gray-200 pb-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h4 className="font-bold text-text-primary">Analytics Cookies</h4>
                <p className="text-sm text-text-secondary mt-1">
                  Help us understand how visitors interact with our website by collecting and reporting
                  information anonymously.
                </p>
              </div>
              <Switch
                checked={preferences.analytics}
                onChange={() => togglePreference('analytics')}
              />
            </div>
          </div>

          {/* Marketing Cookies */}
          <div className="border-b border-gray-200 pb-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h4 className="font-bold text-text-primary">Marketing Cookies</h4>
                <p className="text-sm text-text-secondary mt-1">
                  Used to track visitors across websites to display relevant advertisements and marketing
                  campaigns.
                </p>
              </div>
              <Switch
                checked={preferences.marketing}
                onChange={() => togglePreference('marketing')}
              />
            </div>
          </div>

          {/* Preference Cookies */}
          <div className="pb-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h4 className="font-bold text-text-primary">Preference Cookies</h4>
                <p className="text-sm text-text-secondary mt-1">
                  Remember your settings and preferences to provide you with a personalized experience.
                </p>
              </div>
              <Switch
                checked={preferences.preferences}
                onChange={() => togglePreference('preferences')}
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="secondary"
              text="Cancel"
              onClick={() => setShowSettings(false)}
            />
            <Button
              variant="primary"
              text="Save Preferences"
              onClick={handleSavePreferences}
            />
          </div>
        </div>
      </Modal>
    </>
  );
};
